import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { refreshToken as apiRefreshToken } from '../services/api';
import { jwtDecode } from 'jwt-decode';
import { logger } from '../utils/logger';

const AuthContext = createContext();

const initializeUserSocket = async (userToken) => {
    if (!userToken) return null;
    const { initializeSocket } = await import('../services/socket');
    return initializeSocket(userToken);
};

const isNetworkAuthError = (error) => (
    typeof navigator !== 'undefined' && navigator.onLine === false
) || (
    !error?.response && ['Network Error', 'timeout'].some((text) => error?.message?.includes(text))
);

const SESSION_EXPIRED_KEY = 'eduvibz.sessionExpired';

export const AuthProvider = ({ children }) => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userStatus, setUserStatus] = useState('guest'); // 'guest', 'verified', 'complete'
    const [sessionExpired, setSessionExpired] = useState(() => localStorage.getItem(SESSION_EXPIRED_KEY) === 'true');

    const updateUserStatus = useCallback((userData) => {
        if (!userData) {
            setUserStatus('guest');
        } else if (!userData.preferredCurriculum) {
            setUserStatus('verified');
        } else {
            setUserStatus('complete');
        }
    }, []);

    const clearAuthState = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        delete api.defaults.headers.common['Authorization'];
        setIsLoggedIn(false);
        setUser(null);
        updateUserStatus(null);
    }, [updateUserStatus]);

    const expireSession = useCallback(() => {
        localStorage.setItem(SESSION_EXPIRED_KEY, 'true');
        setSessionExpired(true);
        clearAuthState();
    }, [clearAuthState]);

    const fetchUserData = useCallback(async () => {
        try {
            const response = await api.get('/auth/me');
            logger.debug('Fetched user data', { 
                user: response.data,
                status: response.status 
            });

            const userData = {
                ...response.data,
                sub: response.data.cognitoSub || response.data.sub || response.data['cognito:username'],
                isActive: true // Always set to true - remove subscription dependency
            };

            setUser(userData);
            setIsLoggedIn(true);
            localStorage.removeItem(SESSION_EXPIRED_KEY);
            setSessionExpired(false);
            updateUserStatus(userData);
            return userData;
        } catch (error) {
            logger.error('Failed to fetch user data', { error });
            if (isNetworkAuthError(error) && localStorage.getItem('token')) {
                setIsLoggedIn(true);
                return null;
            }
            expireSession();
            throw error;
        }
    }, [expireSession, updateUserStatus]);

    const refreshUserToken = useCallback(async () => {
        try {
            const refreshTokenValue = localStorage.getItem('refreshToken');
            if (!refreshTokenValue) throw new Error('No refresh token available');

            const response = await apiRefreshToken(refreshTokenValue);
            const { token, refreshToken: nextRefreshToken } = response;
            
            localStorage.setItem('token', token);
            if (nextRefreshToken) {
                localStorage.setItem('refreshToken', nextRefreshToken);
            }
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const refreshedUser = await fetchUserData();
            if (!refreshedUser) {
                logger.warn('Token refreshed but user profile could not be loaded yet; keeping session pending network recovery');
                return;
            }
            logger.debug('Token refreshed successfully');
        } catch (error) {
            logger.error('Failed to refresh token', { error });
            if (isNetworkAuthError(error) && localStorage.getItem('token')) {
                setIsLoggedIn(true);
                return;
            }
            expireSession();
            throw error;
        }
    }, [expireSession, fetchUserData]);

    const checkAuthStatus = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                if (decodedToken.exp * 1000 < Date.now()) {
                    await refreshUserToken();
                } else {
                    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    await fetchUserData();
                }
            } catch (error) {
                logger.error('Auth status check failed', { error });
                if (isNetworkAuthError(error) && token) {
                    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    setIsLoggedIn(true);
                } else {
                    expireSession();
                }
            }
        } else {
            clearAuthState();
        }
        setLoading(false);
    }, [clearAuthState, expireSession, fetchUserData, refreshUserToken]);

    useEffect(() => {
        checkAuthStatus();
    }, [checkAuthStatus]);

    useEffect(() => {
        const handleSessionExpired = () => expireSession();
        window.addEventListener('eduvibz:session-expired', handleSessionExpired);
        return () => window.removeEventListener('eduvibz:session-expired', handleSessionExpired);
    }, [expireSession]);

    const login = useCallback(async (token, refreshTokenValue) => {
        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', refreshTokenValue);
        localStorage.removeItem(SESSION_EXPIRED_KEY);
        setSessionExpired(false);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        try {
            const userData = await fetchUserData();
            if (!userData) {
                throw new Error('Unable to load your user profile. Please try signing in again.');
            }
            logger.info('User logged in successfully', { user: userData });
            const socketUserToken = userData.cognitoSub || userData.sub || userData['cognito:username'];
            setUser({
                ...userData,
                cognitoSub: socketUserToken
            });
            if (socketUserToken) {
                initializeUserSocket(socketUserToken).catch((error) => {
                    logger.warn('Socket initialization deferred after login failed', { error });
                });
            }
        } catch (error) {
            logger.error('Login failed', { error });
            throw error;
        }
    }, [fetchUserData]);

    const logout = useCallback(async () => {
        try {
            await api.post('/auth/logout');
            logger.info('User logged out successfully');
        } catch (error) {
            logger.error('Logout error', { error });
        } finally {
            clearAuthState();
        }
    }, [clearAuthState]);

    const value = {
        isLoggedIn,
        user,
        userStatus,
        login,
        logout,
        refreshUserToken,
        loading,
        sessionExpired,
        checkAuthStatus,
        fetchUserData,
        updateUserStatus
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
